'use client';

import { useState, useEffect, useRef } from 'react';

const RULES = [
  {
    num: '01',
    title: 'Ценим вдохновение и идею',
    text: '— Груша с сыром? — Пробуем! — Карамель и лосось? — Это нам надо! — Угорь, мандарин и воздушные шарики? — Говорим гастро-экспериментам «ДА»!',
    photo: 'https://cdn.aziatochka.ru/aziatochka/09.webp',
  },
  {
    num: '02',
    title: 'Не крутим роллы заранее',
    text: 'Трудовой день сушистов начинается с заготовок и маркировки, а приготовление роллов — только после вашего заказа.',
    photo: 'https://cdn.aziatochka.ru/aziatochka/10.webp',
  },
  {
    num: '03',
    title: 'Сопровождаем на всём пути',
    text: 'От знакомства с продуктом до плотного погружения в обратную связь и корректировку недочётов, если такие случаются!',
    photo: 'https://cdn.aziatochka.ru/aziatochka/11.webp',
  },
  {
    num: '04',
    title: 'Идём до победного',
    text: 'Решаем любые вопросы в турбо-режиме. Мечтаем о партнерстве раз и навсегда, а не на пару покупок.',
    photo: 'https://cdn.aziatochka.ru/aziatochka/12.webp',
  },
  {
    num: '05',
    title: 'Слушаем и совершенствуемся',
    text: 'Знаем, что вам откликается, внедряем и работаем над ошибками. Заменяем личного психолога, если курьер опоздал.',
    photo: 'https://cdn.aziatochka.ru/aziatochka/01.webp',
  },
];

function MobilePhilosophyCard({ rule, index }: { rule: typeof RULES[number]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Once revealed, stays revealed — no flickering
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !revealed) {
          setRevealed(true);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [revealed]);

  return (
    <div
      ref={ref}
      className="about-mobile-card"
      style={{
        opacity: revealed ? 1 : 0.4,
        transform: revealed ? 'translateY(0)' : 'translateY(24px)',
        transition: 'opacity 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        transitionDelay: `${index * 50}ms`,
      }}
    >
      <div
        className="rounded-2xl border overflow-hidden"
        style={{
          background: 'var(--surface-primary)',
          borderColor: revealed ? 'var(--border-hover)' : 'var(--border-default)',
          boxShadow: revealed ? '0 8px 32px rgba(123, 66, 255, 0.06)' : 'none',
          transition: 'border-color 1s ease, box-shadow 1s ease',
        }}
      >
        {/* Photo — height animates smoothly */}
        <div
          className="overflow-hidden"
          style={{
            height: revealed ? '180px' : '0px',
            transition: 'height 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            transitionDelay: revealed ? '200ms' : '0ms',
          }}
        >
          <div className="relative h-[180px]">
            <img
              src={rule.photo}
              alt={rule.title}
              className="w-full h-full object-cover"
              style={{
                transform: revealed ? 'scale(1)' : 'scale(1.1)',
                transition: 'transform 1.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: '300ms',
              }}
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--surface-primary)] via-transparent to-transparent opacity-40" />
            <div className="absolute top-3 left-3">
              <span
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-xs font-numbers font-bold text-[var(--text-on-primary)] shadow-lg"
                style={{ background: 'linear-gradient(135deg, #7B42FF, #5B28D1)' }}
              >
                {rule.num}
              </span>
            </div>
          </div>
        </div>

        <div className="p-5">
          <h3
            className="text-base sm:text-lg font-bold"
            style={{
              color: revealed ? 'var(--text-primary)' : 'var(--text-secondary)',
              transition: 'color 0.6s ease',
            }}
          >
            {rule.title}
          </h3>

          {/* Text — fades in after photo */}
          <div
            style={{
              height: revealed ? 'auto' : '0px',
              opacity: revealed ? 1 : 0,
              overflow: 'hidden',
              marginTop: revealed ? '10px' : '0px',
              transition: 'opacity 0.8s ease, margin-top 0.8s ease',
              transitionDelay: revealed ? '500ms' : '0ms',
            }}
          >
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
              {rule.text}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}


export function AboutPhilosophy() {
  const [active, setActive] = useState(0);

  return (
    <section className="py-20 sm:py-32 relative overflow-hidden">
      <div className="blur-blob absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-[var(--primary)]/4 blur-[150px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16 sm:mb-20">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black tracking-tight text-[var(--text-primary)] leading-[0.95] mb-6 animate-fade-up">
            Почему наши роллы <span className="text-[var(--primary)]">вкусные</span>
          </h2>
          <p className="text-lg sm:text-xl text-[var(--text-secondary)] leading-relaxed max-w-2xl mx-auto animate-fade-up" style={{ animationDelay: '160ms' }}>
            Мы просто очень любим их сами. «Вам — как себе» — основа нашей философии.
          </p>
        </div>

        {/* Desktop: side-by-side photo + accordion */}
        <div className="hidden lg:grid lg:grid-cols-2 gap-10 xl:gap-16 items-start">
          {/* Left — photo that changes with active item */}
          <div className="sticky top-32">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] bg-[var(--surface-tertiary)]">
              {RULES.map((rule, i) => (
                <img
                  key={rule.num}
                  src={rule.photo}
                  alt={rule.title}
                  className="absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out"
                  style={{
                    opacity: active === i ? 1 : 0,
                    transform: active === i ? 'scale(1)' : 'scale(1.08)',
                  }}
                  loading="lazy"
                />
              ))}
              {/* Number overlay */}
              <div className="absolute bottom-6 right-6">
                <span
                  className="text-8xl font-numbers font-black text-white/20 leading-none select-none"
                  style={{ textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}
                >
                  {RULES[active].num}
                </span>
              </div>
              {/* Progress bar */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
                <div
                  className="h-full bg-[var(--primary)] transition-all duration-500 ease-out"
                  style={{ width: `${((active + 1) / RULES.length) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* Right — accordion list */}
          <div className="space-y-0">
            {RULES.map((rule, i) => {
              const isActive = active === i;
              return (
                <button
                  key={rule.num}
                  type="button"
                  onClick={() => setActive(i)}
                  className="w-full text-left group"
                >
                  <div className={`
                    relative py-6 xl:py-8 border-b border-[var(--border-default)] transition-all duration-500
                    ${isActive ? '' : 'hover:bg-[var(--primary-subtle)]/50'}
                  `}>
                    {/* Active indicator line */}
                    <div
                      className="absolute left-0 top-0 bottom-0 w-[3px] rounded-full transition-all duration-500"
                      style={{
                        background: isActive ? 'var(--primary)' : 'transparent',
                        transform: isActive ? 'scaleY(1)' : 'scaleY(0)',
                      }}
                    />

                    <div className="pl-5">
                      {/* Number + title row */}
                      <div className="flex items-baseline gap-4 mb-0">
                        <span className={`
                          text-sm font-numbers font-bold transition-colors duration-300
                          ${isActive ? 'text-[var(--primary)]' : 'text-[var(--text-tertiary)]'}
                        `}>
                          {rule.num}
                        </span>
                        <h3 className={`
                          text-lg xl:text-xl font-bold transition-colors duration-300
                          ${isActive ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)] group-hover:text-[var(--text-primary)]'}
                        `}>
                          {rule.title}
                        </h3>
                      </div>

                      {/* Expandable text */}
                      <div
                        className="overflow-hidden transition-all duration-500 ease-out"
                        style={{
                          maxHeight: isActive ? '200px' : '0px',
                          opacity: isActive ? 1 : 0,
                          marginTop: isActive ? '12px' : '0px',
                        }}
                      >
                        <p className="text-sm xl:text-base text-[var(--text-secondary)] leading-relaxed max-w-lg pl-8">
                          {rule.text}
                        </p>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Mobile: scroll-triggered reveal cards */}
        <div className="lg:hidden space-y-6">
          {RULES.map((rule, i) => (
            <MobilePhilosophyCard key={rule.num} rule={rule} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
